import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, Save, Copy, RefreshCw, Plus, Trash2, GripVertical, 
  Image, Video, AlertTriangle, Lightbulb, Loader2, ExternalLink,
  BookOpen, X, Upload, MessageCircle, FolderOpen
} from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Step Item
const SortableStepItem = ({ step, onEdit, onDelete, isAdmin }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'warning':
        return <Badge className="step-warning"><AlertTriangle className="w-3 h-3 mr-1" />Attenzione</Badge>;
      case 'tip':
        return <Badge className="step-tip"><Lightbulb className="w-3 h-3 mr-1" />Suggerimento</Badge>;
      default:
        return <Badge className="step-normal">Normale</Badge>;
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors"
      data-testid={`step-item-${step.id}`}
    >
      {isAdmin && (
        <button 
          className="drag-handle touch-none" 
          {...attributes} 
          {...listeners}
          data-testid={`drag-handle-${step.id}`}
        >
          <GripVertical className="w-5 h-5" />
        </button>
      )}
      
      <div className="step-number text-sm">
        {step.order}
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{step.title || "Senza titolo"}</h4>
        <div className="flex items-center gap-2 mt-1">
          {getTypeBadge(step.type)}
          {step.images?.length > 0 && (
            <Badge variant="outline" className="text-xs">
              <Image className="w-3 h-3 mr-1" />{step.images.length}
            </Badge>
          )}
          {step.video_type !== 'none' && (
            <Badge variant="outline" className="text-xs">
              <Video className="w-3 h-3 mr-1" />Video
            </Badge>
          )}
        </div>
      </div>
      
      {isAdmin && (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(step)} data-testid={`edit-step-${step.id}`}>
            Modifica
          </Button>
          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => onDelete(step.id)} data-testid={`delete-step-${step.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default function ModelDetailPage({ auth }) {
  const { modelId } = useParams();
  const navigate = useNavigate();
  
  // Check if user is admin
  const isAdmin = auth.user?.role === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [model, setModel] = useState(null);
  const [guide, setGuide] = useState(null);
  const [steps, setSteps] = useState([]);
  const [cards, setCards] = useState([]);
  
  // Step editor
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState({
    title: '', content: '', type: 'normal', images: [],
    video_type: 'none', video_url: '', video_file_url: '', video_caption: ''
  });
  
  // Image upload
  const [uploading, setUploading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // WhatsApp modal
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappName, setWhatsappName] = useState('');
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchModel = async () => {
    try {
      const res = await fetch(`${API}/admin/models/${modelId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!res.ok) throw new Error("Modello non trovato");
      const data = await res.json();
      setModel(data);
      setGuide(data.guide || {});
      setSteps(data.steps || []);
    } catch (err) {
      toast.error(err.message);
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const fetchCards = async () => {
    try {
      const res = await fetch(`${API}/admin/cards`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await res.json();
      setCards(data);
    } catch (err) {
      console.error("Error fetching cards:", err);
    }
  };

  useEffect(() => {
    fetchModel();
    fetchCards();
  }, [modelId]);

  // Save both model and guide
  const handleSave = async () => {
    setSaving(true);
    try {
      // Save model
      const modelRes = await fetch(`${API}/admin/models/${model.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          name: model.name,
          code: model.code,
          card_id: model.card_id || null
        })
      });
      if (!modelRes.ok) {
        const err = await modelRes.json();
        throw new Error(err.detail || "Errore salvataggio modello");
      }
      
      // Save guide
      const guideRes = await fetch(`${API}/admin/guides/${guide.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          title: guide.title,
          intro: guide.intro,
          published: guide.published
        })
      });
      const guideData = await guideRes.json();
      if (!guideRes.ok) throw new Error(guideData.detail || "Errore salvataggio guida");
      
      setGuide(guideData);
      toast.success("Salvato!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRotateToken = async () => {
    if (!window.confirm("Sei sicuro? I link precedenti non funzioneranno più.")) return;
    
    try {
      const res = await fetch(`${API}/admin/guides/${guide.id}/rotate-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore");
      setGuide({ ...guide, public_token: data.public_token });
      toast.success("Link rigenerato!");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyGuideLink = () => {
    if (!guide?.published || !guide?.public_token) {
      toast.error("Pubblica la guida per generare il link");
      return;
    }
    const link = `${window.location.origin}/g/${model.guide_slug}?t=${guide.public_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato!");
  };

  const openWhatsappModal = () => {
    if (!guide?.published || !guide?.public_token) {
      toast.error("Pubblica la guida per inviarla su WhatsApp");
      return;
    }
    setWhatsappPhone('');
    setWhatsappName('');
    setWhatsappModalOpen(true);
  };

  const sendWhatsapp = async () => {
    if (!whatsappPhone) {
      toast.error("Inserisci il numero di telefono");
      return;
    }
    
    // Clean phone number - keep only digits
    let cleanPhone = whatsappPhone.replace(/[^\d]/g, '');
    
    // Remove leading 0 if present
    if (cleanPhone.startsWith('0')) {
      cleanPhone = cleanPhone.substring(1);
    }
    
    // Auto-add Italian prefix 39 if not present
    if (!cleanPhone.startsWith('39')) {
      cleanPhone = '39' + cleanPhone;
    }
    
    // Validate phone number length
    if (cleanPhone.length < 11) {
      toast.error("Numero di telefono non valido");
      return;
    }
    
    // Customer name
    const customerName = whatsappName || 'Cliente';
    
    // Generate guide link with customer info
    const guideLink = `${window.location.origin}/g/${model.guide_slug}?t=${guide.public_token}&phone=${cleanPhone}&name=${encodeURIComponent(customerName)}`;
    
    // Build WhatsApp message
    const message = `Ciao ${customerName}! Ecco la guida: ${guideLink}`;
    
    // Log the send
    try {
      await fetch(`${API}/admin/guide-sends/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          phone_number: cleanPhone,
          customer_name: customerName,
          guide_id: guide.id,
          guide_title: guide.title,
          model_name: model.name
        })
      });
    } catch (e) {
      console.error('Error logging send:', e);
    }
    
    // Build WhatsApp URL
    const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    
    // Close modal first
    setWhatsappModalOpen(false);
    setWhatsappPhone('');
    setWhatsappName('');
    
    // Small delay then open WhatsApp in new tab using anchor click
    setTimeout(() => {
      const a = document.createElement('a');
      a.href = waUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }, 100);
    
    toast.success(`Apertura WhatsApp per +${cleanPhone}`);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = steps.findIndex(s => s.id === active.id);
    const newIndex = steps.findIndex(s => s.id === over.id);
    const newSteps = arrayMove(steps, oldIndex, newIndex).map((s, i) => ({ ...s, order: i + 1 }));
    setSteps(newSteps);
    
    try {
      await fetch(`${API}/admin/guides/${guide.id}/reorder-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify({ step_ids: newSteps.map(s => s.id) })
      });
    } catch (err) {
      toast.error("Errore nel riordinamento");
      fetchModel();
    }
  };

  const handleAddStep = async () => {
    try {
      const res = await fetch(`${API}/admin/guides/${guide.id}/steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify({ title: "Nuovo passo" })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore");
      setSteps([...steps, data]);
      openStepEditor(data);
      toast.success("Passo aggiunto!");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openStepEditor = (step) => {
    setEditingStep(step);
    setStepForm({
      title: step.title || '',
      content: step.content || '',
      type: step.type || 'normal',
      images: step.images || [],
      video_type: step.video_type || 'none',
      video_url: step.video_url || '',
      video_file_url: step.video_file_url || '',
      video_caption: step.video_caption || ''
    });
  };

  const handleSaveStep = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/steps/${editingStep.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify(stepForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore");
      
      setSteps(steps.map(s => s.id === editingStep.id ? data : s));
      setEditingStep(null);
      toast.success("Passo salvato!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm("Eliminare questo passo?")) return;
    
    try {
      const res = await fetch(`${API}/admin/steps/${stepId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!res.ok) throw new Error("Errore");
      setSteps(steps.filter(s => s.id !== stepId).map((s, i) => ({ ...s, order: i + 1 })));
      toast.success("Passo eliminato");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (stepForm.images.length + files.length > 5) {
      toast.error("Massimo 5 immagini per passo");
      return;
    }
    
    setUploading(true);
    for (const file of files) {
      try {
        // Get Cloudinary signature
        const sigRes = await fetch(`${API}/cloudinary/signature?resource_type=image&folder=guideflow`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        const sig = await sigRes.json();
        
        if (!sig.cloud_name || !sig.api_key) {
          toast.error("Cloudinary non configurato");
          continue;
        }
        
        // Upload to Cloudinary
        const form = new FormData();
        form.append("file", file);
        form.append("api_key", sig.api_key);
        form.append("timestamp", String(sig.timestamp));
        form.append("signature", sig.signature);
        form.append("folder", sig.folder);
        
        const uploadRes = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          { method: "POST", body: form }
        );
        const uploadData = await uploadRes.json();
        
        if (uploadData.secure_url) {
          setStepForm(prev => ({
            ...prev,
            images: [...prev.images, uploadData.secure_url]
          }));
        } else if (uploadData.error) {
          toast.error(uploadData.error.message);
        }
      } catch (err) {
        toast.error(`Errore upload: ${file.name}`);
      }
    }
    setUploading(false);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video troppo grande (max 100MB).");
      return;
    }
    
    setUploading(true);
    toast.info("Caricamento video in corso...");
    
    try {
      // Get Cloudinary signature
      const sigRes = await fetch(`${API}/cloudinary/signature?resource_type=video&folder=guideflow`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const sig = await sigRes.json();
      
      if (!sig.cloud_name || !sig.api_key) {
        toast.error("Cloudinary non configurato");
        setUploading(false);
        return;
      }
      
      // Upload to Cloudinary
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", String(sig.timestamp));
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      
      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`,
        { method: "POST", body: form }
      );
      const uploadData = await uploadRes.json();
      
      if (uploadData.secure_url) {
        setStepForm(prev => ({
          ...prev,
          video_type: 'upload',
          video_file_url: uploadData.secure_url
        }));
        toast.success("Video caricato!");
      } else if (uploadData.error) {
        toast.error(uploadData.error.message);
      }
    } catch (err) {
      toast.error("Errore upload video");
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setStepForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">{model?.name}</h1>
                <p className="text-xs text-muted-foreground font-mono">{model?.code}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRotateToken}
                disabled={!guide?.public_token}
                data-testid="rotate-token-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Rigenera link</span>
              </Button>
              <Button 
                variant={guide?.published ? "default" : "secondary"}
                size="sm"
                onClick={copyGuideLink}
                disabled={!guide?.published}
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copia link
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={openWhatsappModal}
                disabled={!guide?.published}
                className="bg-green-600 hover:bg-green-700 text-white border-green-600"
                data-testid="whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">WhatsApp</span>
              </Button>
              {isAdmin && (
                <Button onClick={handleSave} disabled={saving} data-testid="save-guide-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Guide Settings */}
          <div className="lg:col-span-1 space-y-6">
            {/* Model Settings - Only editable by admin */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Modello</CardTitle>
                {!isAdmin && <p className="text-xs text-muted-foreground">Solo visualizzazione</p>}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="model-name">Nome Modello</Label>
                  <Input
                    id="model-name"
                    data-testid="model-name-input"
                    placeholder="Es: Cassa XYZ"
                    value={model?.name || ''}
                    onChange={(e) => setModel({ ...model, name: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model-code">Codice</Label>
                  <Input
                    id="model-code"
                    data-testid="model-code-input"
                    placeholder="Es: CASSA-001"
                    value={model?.code || ''}
                    onChange={(e) => setModel({ ...model, code: e.target.value })}
                    disabled={!isAdmin}
                  />
                  <p className="text-xs text-muted-foreground">Usato per generare il link della guida</p>
                </div>
                {cards.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="model-card" className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Card (categoria)
                  </Label>
                  <Select 
                    value={model?.card_id || "none"} 
                    onValueChange={(value) => setModel({ ...model, card_id: value === "none" ? null : value })}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger data-testid="model-card-select">
                      <SelectValue placeholder="Nessuna card" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna card</SelectItem>
                      {cards.map((card) => (
                        <SelectItem key={card.id} value={card.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: card.color }}
                            />
                            {card.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Assegna questa guida a una categoria (opzionale)
                  </p>
                </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Impostazioni Guida
                </CardTitle>
                {!isAdmin && <p className="text-xs text-muted-foreground">Solo visualizzazione</p>}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="guide-title">Titolo</Label>
                  <Input
                    id="guide-title"
                    data-testid="guide-title-input"
                    placeholder="Es: Come annullare uno scontrino"
                    value={guide?.title || ''}
                    onChange={(e) => setGuide({ ...guide, title: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="guide-intro">Descrizione</Label>
                  <Textarea
                    id="guide-intro"
                    data-testid="guide-intro-input"
                    placeholder="Breve introduzione alla guida..."
                    rows={4}
                    value={guide?.intro || ''}
                    onChange={(e) => setGuide({ ...guide, intro: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label htmlFor="guide-published" className="font-medium">Pubblicata</Label>
                    <p className="text-xs text-muted-foreground">Rendi visibile la guida ai clienti</p>
                  </div>
                  <Switch
                    id="guide-published"
                    data-testid="guide-published-switch"
                    checked={guide?.published || false}
                    onCheckedChange={(checked) => setGuide({ ...guide, published: checked })}
                    disabled={!isAdmin}
                  />
                </div>
                
                {guide?.published && guide?.public_token && (
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-2">Link guida:</p>
                    <code className="text-xs break-all">
                      {window.location.origin}/g/{model?.guide_slug}?t=...
                    </code>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1" onClick={copyGuideLink}>
                        <Copy className="w-3 h-3 mr-1" />
                        Copia
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => window.open(`/g/${model?.guide_slug}?t=${guide?.public_token}`, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Apri
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Steps List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Passi della Guida</CardTitle>
                    <CardDescription>{steps.length} passi</CardDescription>
                  </div>
                  {isAdmin && (
                    <Button onClick={handleAddStep} data-testid="add-step-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi passo
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {steps.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun passo ancora. {isAdmin ? 'Clicca "Aggiungi passo" per iniziare.' : ''}</p>
                  </div>
                ) : (
                  <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {steps.map(step => (
                          <SortableStepItem 
                            key={step.id} 
                            step={step} 
                            onEdit={openStepEditor}
                            onDelete={handleDeleteStep}
                            isAdmin={isAdmin}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Step Editor Dialog */}
      <Dialog open={!!editingStep} onOpenChange={(open) => !open && setEditingStep(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Passo {editingStep?.order}</DialogTitle>
            <DialogDescription>Configura il contenuto di questo passo</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="step-title">Titolo</Label>
              <Input
                id="step-title"
                data-testid="step-title-input"
                value={stepForm.title}
                onChange={(e) => setStepForm({ ...stepForm, title: e.target.value })}
                placeholder="Es: Accedi al menu principale"
              />
            </div>
            
            {/* Type */}
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select 
                value={stepForm.type} 
                onValueChange={(value) => setStepForm({ ...stepForm, type: value })}
              >
                <SelectTrigger data-testid="step-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="warning">Attenzione</SelectItem>
                  <SelectItem value="tip">Suggerimento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="step-content">Contenuto</Label>
              <Textarea
                id="step-content"
                data-testid="step-content-input"
                rows={5}
                value={stepForm.content}
                onChange={(e) => setStepForm({ ...stepForm, content: e.target.value })}
                placeholder="Descrivi questo passo... Usa **testo** per grassetto"
              />
              <p className="text-xs text-muted-foreground">Supporta formattazione base: **grassetto**</p>
            </div>
            
            {/* Media Section - Images OR Video */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Media (Foto o Video)</Label>
              <p className="text-xs text-muted-foreground">Puoi aggiungere immagini, un video, o entrambi per questo passo.</p>
              
              {/* Images */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm">Immagini ({stepForm.images.length}/5)</Label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {stepForm.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img 
                        src={img} 
                        alt={`Immagine ${i + 1}`}
                        className="w-full h-24 object-cover rounded-lg cursor-pointer"
                        onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeImage(i)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {stepForm.images.length < 5 && (
                    <label className="upload-zone flex flex-col items-center justify-center h-24 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        data-testid="image-upload-input"
                      />
                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground">Carica foto</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>
            </div>
            
            {/* Video */}
            <div className="space-y-3 p-3 border rounded-lg">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm">Video</Label>
              </div>
              <Select 
                value={stepForm.video_type} 
                onValueChange={(value) => setStepForm({ ...stepForm, video_type: value, video_url: '', video_file_url: '' })}
              >
                <SelectTrigger data-testid="video-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun video</SelectItem>
                  <SelectItem value="url">Link video (YouTube/Vimeo/mp4)</SelectItem>
                  <SelectItem value="upload">Carica video</SelectItem>
                </SelectContent>
              </Select>
              
              {stepForm.video_type === 'url' && (
                <div className="space-y-2">
                  <Input
                    data-testid="video-url-input"
                    placeholder="https://www.youtube.com/watch?v=... o link mp4"
                    value={stepForm.video_url}
                    onChange={(e) => setStepForm({ ...stepForm, video_url: e.target.value })}
                  />
                </div>
              )}
              
              {stepForm.video_type === 'upload' && (
                <div className="space-y-2">
                  {stepForm.video_file_url ? (
                    <div className="relative">
                      <video src={stepForm.video_file_url} controls className="w-full rounded-lg" />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setStepForm({ ...stepForm, video_file_url: '' })}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Rimuovi
                      </Button>
                    </div>
                  ) : (
                    <label className="upload-zone flex flex-col items-center justify-center py-8 cursor-pointer">
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={handleVideoUpload}
                        disabled={uploading}
                        data-testid="video-upload-input"
                      />
                      {uploading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      ) : (
                        <>
                          <Video className="w-8 h-8 text-muted-foreground mb-2" />
                          <span className="text-sm text-muted-foreground">Clicca per caricare (max 200MB)</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              )}
              
              {stepForm.video_type !== 'none' && (
                <Input
                  data-testid="video-caption-input"
                  placeholder="Didascalia video (opzionale)"
                  value={stepForm.video_caption}
                  onChange={(e) => setStepForm({ ...stepForm, video_caption: e.target.value })}
                />
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStep(null)}>
              Annulla
            </Button>
            <Button onClick={handleSaveStep} disabled={saving} data-testid="save-step-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salva passo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={stepForm.images.map(src => ({ src }))}
      />

      {/* WhatsApp Modal */}
      <Dialog open={whatsappModalOpen} onOpenChange={setWhatsappModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              Invia su WhatsApp
            </DialogTitle>
            <DialogDescription>
              Invia il link della guida "{guide?.title}" direttamente su WhatsApp
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="whatsapp-phone">Numero di telefono *</Label>
              <Input
                id="whatsapp-phone"
                data-testid="whatsapp-phone-input"
                placeholder="Es: 3331234567"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Solo il numero italiano (il prefisso 39 viene aggiunto automaticamente)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="whatsapp-name">Nome cliente (opzionale)</Label>
              <Input
                id="whatsapp-name"
                data-testid="whatsapp-name-input"
                placeholder="Es: Mario"
                value={whatsappName}
                onChange={(e) => setWhatsappName(e.target.value)}
              />
            </div>
            
            {/* Preview */}
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Anteprima messaggio:</p>
              <p className="text-sm whitespace-pre-line">
                {whatsappName ? `Ciao ${whatsappName}!` : 'Ciao!'}
                {'\n\n'}Ti invio la guida: {guide?.title}
                {guide?.intro && `\n\n${guide.intro}`}
                {'\n\n'}Clicca qui per aprirla:
                {'\n'}[link guida]
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappModalOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={sendWhatsapp}
              className="bg-green-600 hover:bg-green-700"
              data-testid="send-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Apri WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
